/// <amd-module name="Wit/Common/WorkItemDialogs/Components/EditTemplateDialog" />
import * as React from "react";
import { ITfsTeamService } from "TFS/Services/TfsTeamService";
import { ITeamData } from "Tfs/Platform/Page";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { equals, format, localeIgnoreCaseComparer, newGuid, startsWith } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { IVssPageService } from "VSS/Platform/Page";
import { Button } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem } from "VSSUI/ListBox";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { TextField } from "VSSUI/TextField";
import { copyToClipboard } from "VSSUI/Utils/ClipboardUtils";
import { WorkItemTemplate } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { performBulkUpdate } from "Wit/Common/BulkUpdateUtils/BulkUpdateUtils";
import { AddTagsPseudoRefName } from "Wit/Common/BulkUpdateUtils/TagUtils";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { MacroToday, buildDate, tryConvertStringToDate } from "Wit/Common/DateUtils";
import { CoreField, CoreFieldRefNames, FieldType } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { getCurrentCultureFromLegacyContext } from "Wit/Common/Utilities/FormatUtils";
import { WiqlOperators, isMeMacro } from "Wit/Common/WiqlOperators";
import { BulkEditFieldsTable, Clause, isExcludedFromBulkEdit } from "Wit/Common/WorkItemDialogs/Components/BulkEditFieldsTable";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IWorkItemTemplateService } from "Wit/Common/WorkItemTemplateService/IWorkItemTemplateService";
import { HtmlEditor } from "Wit/HtmlEditor/Components/HtmlEditor";
import {
    convertWorkItemIdentityRefFromFieldValue,
    getDistinctDisplayName,
    isWorkItemIdentityRef
} from "Wit/Identity/Utilities/WorkItemIdentityHelper";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemEventService, IWorkItemEventType } from "Wit/WorkItemModel/Services/IWorkItemEventService";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";

export interface IEditTemplateDialogProps {
    fields: IFieldEntry[];
    onDismiss: () => void;
    onSave?: () => void;
    selectedItem?: INamedFieldDataDictionary;
    teamId?: string;
    workItemTemplate?: WorkItemTemplate;
}

export interface IEditTemplateDialogState {
    errorMessage?: string;
    isValid: boolean;
    selectedTypeFields: IFieldEntry[];
    working: boolean;
    workItemTemplate?: WorkItemTemplate;
}

const formRoute = "ms.vss-work-web.new-work-item-form-route";

export class EditTemplateDialog extends TfsComponent<IEditTemplateDialogProps, IEditTemplateDialogState> {
    private templateName: IObservableValue<string>;
    private templateDescription: IObservableValue<string>;
    private team = new ObservableValue("");
    private teams = new ObservableArray<IListBoxItem>([]);
    private clauses = new ObservableArray<Clause>([]);
    private historyNotes = new ObservableValue("");
    private isNewTemplate = false;
    private isTableValid = true;

    constructor(props: IEditTemplateDialogProps, context: IVssComponentContext) {
        super(props, context);
        this.isNewTemplate = !props.workItemTemplate;
        this.templateDescription = new ObservableValue(props.workItemTemplate?.description ?? "");
        this.templateName = new ObservableValue(props.workItemTemplate?.name ?? "");
        this.state = {
            isValid: true,
            selectedTypeFields: [],
            working: true,
            workItemTemplate: props.workItemTemplate
        };
    }

    public async componentDidMount() {
        if (this.isNewTemplate && this.projectData) {
            const teamService = this.context.pageContext.getService<ITfsTeamService>("ITfsTeamService");
            const teams = await teamService.getAllTeams(this.projectData.id);
            this.teams.value = teams.sort((t1, t2) => localeIgnoreCaseComparer(t1.name, t2.name)).map(team => ({ text: team.name, id: team.id }));
        }

        let fields: INamedFieldDataDictionary | undefined = this.props.workItemTemplate?.fields;
        let selectedTypeFields: IFieldEntry[] = [];

        // NOTE: if this dialog is ever used on the template settings page, async fetching fields will
        // need to be implemented. For now, we can just use the fields off the selected work item.
        if (!fields && this.props.selectedItem && this.props.selectedItem[CoreFieldRefNames.Id] !== undefined) {
            const workItemId = Number(this.props.selectedItem[CoreFieldRefNames.Id]);
            const workItemModelService = this.context.pageContext.getService<IWorkItemModelService>("IWorkItemModelService");
            const workItem = await workItemModelService.getWorkItem(workItemId);
            const workItemFields = Object.assign({}, workItem.getFieldData(), workItem.getFieldUpdates());
            selectedTypeFields = workItem.fields.map(field => field.fieldDefinition);

            // create map of supported fields from work item data
            const filteredFields: INamedFieldDataDictionary = {};
            for (const fieldKey of Object.keys(workItemFields)) {
                const fieldId = Number(fieldKey);
                const value = workItemFields[fieldId];
                const field = selectedTypeFields.find(field => field.id === Number(fieldId));
                if (field) {
                    if (field.id === CoreField.Tags) {
                        filteredFields[AddTagsPseudoRefName] = value;
                    } else if (!isExcludedFromBulkEdit(field)) {
                        filteredFields[field.referenceName] = value;
                    }
                }
            }

            fields = filteredFields;
        }

        if (fields) {
            this.clauses.value = await this.getClausesFromFieldValues(fields, !this.isNewTemplate);
        }

        this.validate();
        this.setState({ selectedTypeFields, working: false });
    }

    public render(): JSX.Element {
        const { onDismiss } = this.props;
        const { errorMessage, isValid, selectedTypeFields, working, workItemTemplate } = this.state;
        return (
            <Dialog
                calloutContentClassName="work-item-dialog"
                titleProps={{ text: Resources.CaptureTemplateHeader }}
                onDismiss={onDismiss}
                footerButtonProps={[
                    {
                        text: Resources.CloseLabel,
                        onClick: onDismiss
                    },
                    {
                        text: Resources.SaveLabel,
                        disabled: !isValid || working,
                        onClick: this.onSave,
                        primary: true
                    }
                ]}
                overlay={working ? { spinnerAriaLabel: Resources.CreatingTemplateSpinnerAriaLabel } : undefined}
                resizable={true}
                showCloseButton={true}
            >
                <div className="rhythm-vertical-16">
                    {errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{errorMessage}</MessageCard>}
                    {this.isNewTemplate && (
                        <FormItem label={<FormLabel text={Resources.TeamLabel} required />}>
                            {/** after the template gets saved, the team can't be updated */}
                            <Dropdown
                                disabled={!!workItemTemplate}
                                items={this.teams}
                                onSelect={(event, item) => {
                                    this.team.value = item.id;
                                    this.validate();
                                }}
                            />
                        </FormItem>
                    )}
                    <FormItem label={<FormLabel text={Resources.NameLabel} required />}>
                        <TextField
                            value={this.templateName}
                            onChange={(event, value) => {
                                this.templateName.value = value;
                                this.validate();
                            }}
                        />
                    </FormItem>
                    <FormItem label={<FormLabel text={Resources.DescriptionLabel} />}>
                        <TextField
                            value={this.templateDescription}
                            onChange={(event, value) => {
                                this.templateDescription.value = value;
                                this.validate();
                            }}
                        />
                    </FormItem>
                    <div className="margin-bottom-16">
                        <BulkEditFieldsTable
                            clauses={this.clauses}
                            fields={selectedTypeFields}
                            getAllowedValues={this.getAllowedValues}
                            onValidate={this.onValidateTable}
                            projectId={this.projectData?.id ?? ""}
                            scrollable={true}
                        />
                        <Button iconProps={{ iconName: "Cancel" }} onClick={this.removeUnmodifiedClauses} text={Resources.RemoveFieldsButtonLabel} />
                    </div>
                </div>
                <HtmlEditor
                    className="history-input"
                    ariaLabel={Resources.EditWorkItemsNotesAriaLabel}
                    placeholder={Resources.EditWorkItemsNotes}
                    htmlContent={this.historyNotes.value}
                    onChange={(newValue: string) => {
                        this.historyNotes.value = newValue;
                    }}
                    telemetryContext={{ controlName: "HtmlEditor" }}
                    showChromeBorder={true}
                />
                <div className="margin-top-16">
                    <Button
                        disabled={!workItemTemplate}
                        iconProps={{ iconName: "Copy" }}
                        onClick={event => {
                            if (workItemTemplate) {
                                copyToClipboard(this.getCreateWorkItemWithTemplateRoute(workItemTemplate));
                            }
                        }}
                        text={Resources.CopyLink}
                        tooltipProps={{ text: workItemTemplate ? Resources.CopyTemplateLinkMessage : Resources.SaveTemplateMessage }}
                    />
                </div>
            </Dialog>
        );
    }

    /**
     * Get the allowed values for a group of work item types in a project
     */
    private getAllowedValues = async (field: IFieldEntry): Promise<string[] | undefined> => {
        if (!this.projectData || field.referenceName === CoreFieldRefNames.AreaPath || field.referenceName === CoreFieldRefNames.IterationPath) {
            return;
        }

        const workItemTypeName = this.props.workItemTemplate?.workItemTypeName ?? this.props.selectedItem![CoreFieldRefNames.WorkItemType];
        const fieldService = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.UseNewWorkItemFieldService, false)
                    ? this.context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2")
                    : this.context.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
        const allowedValues = await fieldService.getAllowedValues(field.referenceName, this.projectData.id, workItemTypeName);

        return allowedValues;
    };

    private removeUnmodifiedClauses = (): void => {
        this.clauses.removeAll(clause => !clause.dirty);
    };

    private onSave = async () => {
        if (!this.state.isValid || (!this.props.selectedItem && !this.state.workItemTemplate)) {
            return;
        }

        const fields: { [key: string]: string } = {};
        for (const clause of this.clauses.value) {
            if (clause.field.value) {
                const value = isWorkItemIdentityRef(clause.value.value) ? clause.value.value.distinctDisplayName : clause.value.value?.toString();
                fields[clause.field.value.referenceName] = value ?? "";
            }
        }

        if (this.historyNotes.value !== "") {
            fields[CoreFieldRefNames.History] = this.historyNotes.value;
        }

        const workItemTypeName = this.state.workItemTemplate?.workItemTypeName ?? this.props.selectedItem![CoreFieldRefNames.WorkItemType];
        const workItemTemplate = {
            description: this.templateDescription.value,
            fields,
            id: this.state.workItemTemplate?.id,
            name: this.templateName.value,
            workItemTypeName
        } as WorkItemTemplate;

        this.setState({ working: true });
        const templateService = this.context.pageContext.getService<IWorkItemTemplateService>("IWorkItemTemplateService");
        try {
            const projectId = this.projectData?.id ?? "";
            const createdTemplate = this.state.workItemTemplate
                ? await templateService.replaceTemplate(projectId, this.props.teamId ?? this.team.value, workItemTemplate)
                : await templateService.createTemplate(projectId, this.team.value, workItemTemplate);

            // set isValid to false to disable save button until user makes changes again
            this.setState({ working: false, workItemTemplate: { ...createdTemplate, fields: workItemTemplate.fields }, isValid: false });
            if (this.props.onSave) {
                this.props.onSave();
            }
        } catch (e) {
            this.setState({ errorMessage: e.message, working: false, isValid: false });
        }
    };

    private onValidateTable = (isTableValid: boolean) => {
        this.isTableValid = isTableValid;
        this.validate();
    };

    private validate(): void {
        const isValid = !!this.team.value && !!this.templateName.value && !!this.clauses.length && this.isTableValid;
        if (isValid !== this.state.isValid) {
            this.setState({ isValid });
        }
    }

    private async getClausesFromFieldValues(fields: INamedFieldDataDictionary, includeEmptyFields = false): Promise<Clause[]> {
        const clausePromises: Promise<Clause>[] = [];
        for (const fieldName of this.sortNamespacesByName(Object.keys(fields))) {
            const value = fields[fieldName];
            let field = this.props.fields.find(field => field.referenceName === fieldName);
            if (fieldName === AddTagsPseudoRefName)
                field = this.props.fields.find(field => field.referenceName === CoreFieldRefNames.Tags);

            if (field && (includeEmptyFields || value)) {
                if (field.id === CoreField.Tags) {
                    field = { ...field, name: format(Resources.AddTagsFormat, field.name), referenceName: AddTagsPseudoRefName };
                }

                clausePromises.push(this.createClauseWithAllowedValues(field, value));
            }
        }

        return Promise.all(clausePromises);
    }

    private async createClauseWithAllowedValues(field: IFieldEntry, value: any): Promise<Clause> {
        const allowedValues = await this.getAllowedValues(field);
        return {
            allowedValues: new ObservableArray<string>(allowedValues ?? []),
            field: new ObservableValue(field),
            id: newGuid(),
            value: new ObservableValue(value)
        };
    }

    private getCreateWorkItemWithTemplateRoute(workItemTemplate: WorkItemTemplate) {
        const teamId = this.props.teamId || this.team.value;
        const { id, workItemTypeName } = workItemTemplate;
        const routeValues = {
            project: this.projectData?.id ?? "",
            workItemTypeName,
            templateId: id,
            ownerId: teamId
        };

        const baseUrl = window.location.origin;
        const generatedUrl = routeUrl(this.context.pageContext, formRoute, routeValues);
        const templateUrl = `${baseUrl}${generatedUrl.replace("/?", "?")}`;
        return templateUrl;
    }

    private sortNamespacesByName(source: string[]): string[] {
        return source.sort((nameSpaceA, nameSpaceB) => {
            const lastPartA = nameSpaceA.split(".").pop() || "";
            const lastPartB = nameSpaceB.split(".").pop() || "";

            return lastPartA < lastPartB ? -1 : lastPartA > lastPartB ? 1 : 0;
        });
    }
}

function FormLabel({ text, required }: { text: string; required?: boolean }) {
    return (
        <div className="flex-row">
            <div className="flex-grow">{text}</div>
            {required && <div className="secondary-text">{Resources.RequiredLabel}</div>}
        </div>
    );
}

async function loadTemplateSubMenuItems(
    subMenuItems: IObservableArray<IMenuItem>,
    pageContext: IVssPageContext,
    selectedItems: INamedFieldDataDictionary[],
    openDialog: (callout: (onDismiss: () => void) => JSX.Element) => void,
    projectId: string,
    optionalParams?: {
        teamData?: ITeamData,
        fields?: ObservableArray<IFieldEntry>,
        onModifyItems?: (updatedItems: IWorkItem[]) => void,
        currentIterationPath?: string
    }
) {
    let fields = optionalParams?.fields;
    if (fields === undefined || fields === null) {
        let loadedFields : IFieldEntry[] | undefined = undefined;
        if (isFeatureFlagEnabled(pageContext, FeatureFlags.UseNewWorkItemFieldService, false)){
            const fieldService = pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            loadedFields = await fieldService.loadFields();
        } else {
            const fieldService = pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            loadedFields = await fieldService.getFieldsAsync();
        }
        fields = new ObservableArray<IFieldEntry>(loadedFields || []);
    }
    // All selected items must have the same type.
    const workItemType = selectedItems[0][CoreFieldRefNames.WorkItemType];
    const updatedItems: IMenuItem[] = [
        {
            groupKey: "capture-template",
            id: "capture-work-item",
            onActivate: () => {
                openDialog(onDismiss => (
                    <Observer fields={fields}>
                        {(observerProps: { fields: IFieldEntry[] }) => (
                            <EditTemplateDialog
                                fields={observerProps.fields}
                                onDismiss={onDismiss}
                                onSave={() =>
                                    loadTemplateSubMenuItems(
                                        subMenuItems,
                                        pageContext,
                                        selectedItems,
                                        openDialog,
                                        projectId,
                                        {
                                            teamData: optionalParams?.teamData,
                                            fields: fields!,
                                            currentIterationPath: optionalParams?.currentIterationPath
                                        })
                                }
                                selectedItem={selectedItems[0]}
                            />
                        )}
                    </Observer>
                ));
            },
            text: Resources.WorkItemTemplateCapture,
            iconProps: { iconName: "Camera" },
            disabled: selectedItems.length > 1
        }
    ];

    if (optionalParams?.teamData && workItemType) {
        const templateService = pageContext.getService<IWorkItemTemplateService>("IWorkItemTemplateService");
        const templates = await templateService.getMyTemplateReferences(projectId, optionalParams.teamData.id, workItemType);
        updatedItems.push(
            ...templates.map(template => ({
                id: template.id,
                onActivate: () => {
                    applyTemplate(template.id, pageContext, selectedItems, projectId, fields!, optionalParams.teamData, optionalParams.onModifyItems, optionalParams.currentIterationPath);
                },
                text: template.name
            })).sort((a, b) => a.text.localeCompare(b.text, navigator.language))
        );
    }

    subMenuItems.value = updatedItems;
}

async function applyTemplate(
    templateId: string,
    pageContext: IVssPageContext,
    selectedItems: INamedFieldDataDictionary[],
    projectId: string,
    fields: ObservableArray<IFieldEntry>,
    teamData?: ITeamData,
    onModifyItems?: (updatedItems: IWorkItem[]) => void,
    currentIterationPath?: string
) {
    if (!teamData) {
        return;
    }

    const workItemIds = selectedItems.map(selectedItem => Number(selectedItem[CoreFieldRefNames.Id]));
    const templateService = pageContext.getService<IWorkItemTemplateService>("IWorkItemTemplateService");
    const template = await templateService.getTemplate(projectId, teamData.id, templateId);
    const changesList: IWorkItemFieldChange[] = [];
    const currentCulture = getCurrentCultureFromLegacyContext();
    for (const fieldName in template.fields) {
        let value: any = template.fields[fieldName];
        const field = fields.value.find(field => field.referenceName === fieldName);

        if (field) {
            switch (field.type) {
                case FieldType.DateTime:
                    const parsedDateValue = typeof value === "string" && startsWith(value, MacroToday, /* ignore case */ true)
                        ? buildDate(value)
                        : tryConvertStringToDate(value, { currentCulture, format: "d" });

                    if (parsedDateValue && !isNaN(parsedDateValue.getTime())) {
                        value = parsedDateValue;
                    }
                    break;
                case FieldType.Integer:
                case FieldType.Boolean:
                    value = Number(value);
                    break;
                case FieldType.TreePath:
                    if (field.referenceName === CoreFieldRefNames.IterationPath && currentIterationPath) {
                        if (typeof value === "string" && equals(value, WiqlOperators.MacroCurrentIteration, /* ignore case */ true)) {
                            value = currentIterationPath;
                        }
                    }
                default:
                    if (field.isIdentity && value) {
                        if (isMeMacro(value, false)) {
                            const currentUser = pageContext.getService<IVssPageService>("IVssPageService").getData()?.user;
                            value = currentUser ? getDistinctDisplayName(currentUser.displayName, currentUser.uniqueName) : value;
                        }
                        value = convertWorkItemIdentityRefFromFieldValue(pageContext, value);
                    }
                    break;
            }
        }

        changesList.push({ fieldName, value });
    }

    const changesMap = new Map<string, IWorkItemFieldChange[]>([[BulkUpdateConstants.AllTypes, changesList]]);
    const bulkUpdateResult = await performBulkUpdate(pageContext, workItemIds, changesMap);

    const workItemEventService = pageContext.getService<IWorkItemEventService>("IWorkItemEventService");
    bulkUpdateResult.forEach(workItem => {
        workItemEventService.fireEvent({ workItem }, IWorkItemEventType.WorkItemTemplateApplied);
    });

    if (onModifyItems) {
        onModifyItems(bulkUpdateResult);
    }
}

export function getTemplateMenuItem(
    pageContext: IVssPageContext,
    selectedItems: INamedFieldDataDictionary[],
    openDialog: (callout: (onDismiss: () => void) => JSX.Element) => void,
    projectId: string,
    optionalParams?: {
        teamData?: ITeamData,
        fields?: ObservableArray<IFieldEntry>,
        onModifyItems?: (updatedItems: IWorkItem[]) => void,
        currentIterationPath?: string
    }
) {
    const subMenuItems = new ObservableArray<IMenuItem>();
    loadTemplateSubMenuItems(subMenuItems, pageContext, selectedItems, openDialog, projectId, optionalParams);
    return {
        rank: 70,
        id: "work-item-templates",
        text: Resources.WorkItemTemplate,
        iconProps: { iconName: "AutoFillTemplate" },
        groupKey: "modify",
        subMenuProps: {
            id: "work-item-templates-sub-menu",
            items: subMenuItems
        }
    };
}
